import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Loading from "../../common/loading";

import axios from "axios";

import { hideLoading, showLoading } from "../../store/loading";
import uuid from "react-uuid";
import { GroupContainer } from "./component/styled/common";
import PageInfo from "./component/common/pageInfo";
import { CreateFeedContainer } from "./component/styled/feedWrite";
import Info from "./component/feedWrite/info";
import Form from "./component/feedWrite/form";
import { useModal } from "../../hooks/useModal";
import Actions from "./component/feedWrite/actions";
import useUploadFiles from "../../hooks/useUploadFiles";

const FeedWrite = () => {
  const user = useSelector((state) => state?.user?.data[0]);
  const isLoading = useSelector((state) => state?.loading?.isLoading);

  const { modal, openModal, closeModal } = useModal();
  const { uploadFiles, dataURItoBlob, readFileAsDataURL } = useUploadFiles();

  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const origin = location?.state?.feed;

  const [my, setMy] = useState([]);
  const [feed, setFeed] = useState({ nickNm: "", gbn: 0, groupName: "", imgUrl: [], content: "" });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!user) {
      toast.error("로그인 후 이용 가능합니다.", { toastId: "no-access" });
      navigate("/group");
    } else {
      getMyGroup();
    }

    if (origin) {
      const updated =
        (origin.imgUrl &&
          JSON.parse(origin.imgUrl).map((item) => ({
            path: item,
            id: uuid(),
          }))) ||
        [];

      setFeed({
        ...origin,
        imgUrl: updated,
        content: origin.realContent ? origin.realContent : origin.content || "",
      });
    }
  }, []);

  const getMyGroup = async () => {
    const url = "/api/groupCondList";
    const req = getDataCond();
    const res = await axios.post(url, req);
    if (res.status === 200) {
      setMy(res.data.content);
    }
  };

  const getDataCond = () => {
    return {
      mberNo: user.mberNo,
      delYn: "N",
      offset: 0,
      pageNumber: 1,
      pageSize: 1,
      paged: false,
    };
  };

  const checkValidation = async () => {
    if (feed.feedNo) {
      const delCheck = await feedDelYnCheck(feed.feedNo);
      if (delCheck === "Y") {
        toast.error("게시물이 존재하지 않습니다.", { toastId: "no-access" });
        navigate("/group/feed");
        return false;
      }
    }

    if (feed.gbn === "0" || !feed.gbn) {
      toast.error("모임을 선택해주세요.");
      return false;
    }

    if (!feed.content || !feed.content.trim() || !feed.content.replaceAll("\n", "")) {
      toast.error("내용이 존재하지 않습니다.");
      return false;
    }
    return true;
  };

  // 수정 완료 시 삭제여부
  const feedDelYnCheck = async (feedNo) => {
    const url = "/api/feedList";
    const request = {
      feedNo,
      offset: 0,
      pageNumber: 0,
      pageSize: 10,
      paged: true,
    };

    const res = await axios.post(url, request);
    return res?.data?.content[0].delYn;
  };

  // 이미지 업로드
  const uploadImages = async () => {
    try {
      const base64Items = feed.imgUrl.filter((item) => item.path.startsWith("data:image/"));

      const filesToUpload = base64Items.map((item) => {
        const blob = dataURItoBlob(item.path);
        const ext = blob.type.split("/")[1];
        return new File([blob], `${item.id}.${ext}`, { type: blob.type });
      });

      const uploaded = await uploadFiles(filesToUpload);

      return uploaded.map((item, idx) => ({
        id: base64Items[idx].id,
        fileName: item.fileName,
      }));
    } catch (err) {
      console.error("이미지 업로드 실패", err);
      return [];
    }
  };

  // 업로드된 이미지로 feed.imgUrl 대체
  const replaceImages = (imgUrlList, uploadedList) => {
    return imgUrlList.map((item) => {
      const matched = uploadedList.find((u) => u.id === item.id);
      return matched ? matched.fileName : item.path;
    });
  };

  // 최종 제출
  const handleSubmit = async () => {
    if (!(await checkValidation())) {
      closeModal();
      return;
    }

    try {
      dispatch(showLoading());

      let uploadedImages = [];
      if (feed.imgUrl.length > 0) {
        const serverData = await uploadImages();
        uploadedImages = replaceImages(feed.imgUrl, serverData);
      }

      const url = "/api/feedList";
      const body = {
        content: feed.content.trim(),
        imgUrl: uploadedImages ? JSON.stringify(uploadedImages) : JSON.stringify([]),
        mberNo: user.mberNo,
        mberNm: user.name,
        delYn: "N",
        gbn: feed.gbn,
        createMberId: user.userName,
        updateMberId: user.userName,
        ...(feed.feedNo && { feedNo: feed.feedNo }),
      };

      const res = await axios.put(url, body);

      if (res.status === 200) {
        toast.success(`게시글이 ${feed.createDt ? "수정" : "작성"}되었습니다.`, {
          toastId: "no-access",
        });
        const groupNo = location?.state?.groupNo;
        navigate(groupNo ? `/group/${groupNo}/community` : "/group/feed");
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideLoading());
    }
  };

  // 이미지 추가
  const changeImg = async (e) => {
    const inputFiles = e.target.files;
    if (!inputFiles) return;

    const updatedImages = [];
    const updatedFiles = [];

    for (const file of inputFiles) {
      const id = uuid();
      const path = await readFileAsDataURL(file);
      updatedImages.push({ path, id });
      updatedFiles.push({ file, id });
    }

    setFeed((prev) => ({
      ...prev,
      imgUrl: [...prev.imgUrl, ...updatedImages],
    }));

    setFiles((prev) => [...prev, ...updatedFiles]);
  };

  // 클립보드
  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    const imageFilePromises = [];

    for (let item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        const mockEvent = { target: { files: [file] } };
        imageFilePromises.push(changeImg(mockEvent));
      }
    }
  };

  const deleteImages = (item) => {
    openModal({
      isOpen: true,
      type: "confirmation",
      title: "이미지 삭제",
      message: [`이미지를 삭제하시겠습니까?`],
      handleConfirm: () => {
        setFeed((data) => ({
          ...data,
          imgUrl: [...data.imgUrl].filter((ele) => ele.id !== item.id),
        }));
        setFiles(files.filter((img) => img.id !== item.id));
        closeModal();
      },
      handleCancel: () => closeModal(),
    });
  };

  return (
    <GroupContainer>
      {modal}
      <div className="inner">
        <PageInfo title="MOIM - 협력과 소통의 공간" />
        <CreateFeedContainer>
          <Info
            feed={feed}
            setFeed={setFeed}
            location={location}
            myGrp={my}
            changeImg={changeImg}
          />
          <Form
            feed={feed}
            setFeed={setFeed}
            handleModal={deleteImages}
            handlePaste={handlePaste}
          />
          <Actions
            feed={feed}
            openModal={openModal}
            closeModal={closeModal}
            handleSubmit={handleSubmit}
          />
        </CreateFeedContainer>
      </div>
      {isLoading && <Loading />}
    </GroupContainer>
  );
};

export default FeedWrite;
